import React, { useState } from 'react';
import { Button, Menu, MenuItem, MenuProps } from '@mui/material';
import { UserPermission, colors } from 'remote/global';
import { useTranslation } from 'react-i18next';
import { ExpandMore } from '@mui/icons-material';
import { AddEventTypeEnum, SchedulerTypeEnum } from 'utils/data/data-types';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { isDev } from '@/utils/helpers';
import { track } from '@/utils/tracking';

interface Props {
  schedulerType?: SchedulerTypeEnum;
  handleEventClick: (option: AddEventTypeEnum) => void;
}

interface DropdownListProps extends Props, MenuProps {
  open: boolean;
  handleClose?: (exitWithoutSelection?: boolean) => void;
  addEventOptions?: AddEventTypeEnum[];
}

export function AddEventDropdownList({
  schedulerType,
  handleEventClick,
  handleClose,
  open,
  addEventOptions,
  ...rest
}: DropdownListProps) {
  const { t } = useTranslation();
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions = {} } = userInfo || {};

  const getListOptions = () => {
    if (addEventOptions != null && Array.isArray(addEventOptions)) return addEventOptions;
    switch (schedulerType) {
      case SchedulerTypeEnum.BUS_SCHEDULER:
        return [
          ...(![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(permissions.ORDER)
            ? [AddEventTypeEnum.RIDE]
            : []),
          ...(![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
            permissions.OPERATOR_OFFER
          )
            ? [AddEventTypeEnum.OFFER]
            : []),
          ...(![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(permissions.RIDE)
            ? [AddEventTypeEnum.OFFLINE]
            : []),
          ...(isDev &&
          ![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(permissions.RIDE)
            ? [AddEventTypeEnum.REGULAR_TRIP]
            : []),
          ...(![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
            permissions.DRIVER_SCHEDULE
          ) &&
          ![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
            permissions.VEHICLE_SCHEDULE
          )
            ? [AddEventTypeEnum.REPAIR]
            : []),
        ];
      case SchedulerTypeEnum.DRIVER_WORK_SCHEDULER:
        return [
          ...(![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
            permissions.DRIVER_SCHEDULE
          )
            ? [AddEventTypeEnum.VACATION]
            : []),
        ];
      case SchedulerTypeEnum.DRIVER_SCHEDULER:
        return [
          ...(![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
            permissions.DRIVER_SCHEDULE
          )
            ? [AddEventTypeEnum.VACATION]
            : []),
        ];
      default:
        return [];
    }
  };

  const options = getListOptions();

  if (options.length <= 0) return null;

  return (
    <Menu
      open={open}
      onClose={() => {
        if (typeof handleClose === 'function') handleClose(true);
      }}
      {...rest}
    >
      {options.map((option, idx) => {
        const result = [
          ...(idx === 0 && addEventOptions != null
            ? [
                // eslint-disable-next-line react/jsx-indent
                <MenuItem
                  key='add-event-title'
                  divider
                  disabled
                  sx={{ cursor: 'not-allowed', color: colors.uiBlue600, opacity: '1!important' }}
                >
                  {t(`SchedulerView.createEvent.title`)}
                </MenuItem>,
              ]
            : []),
          <MenuItem
            key={option}
            data-testid={`add-event-${option}`}
            onClick={() => {
              handleEventClick(option);
              track.partner_button_click(option);
              if (typeof handleClose === 'function') handleClose(false);
            }}
          >
            {t(`SchedulerView.createEvent.eventType.${option}.title`)}
          </MenuItem>,
        ];

        return result;
      })}
    </Menu>
  );
}

function AddEventDropdown({ schedulerType, handleEventClick }: Props) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button endIcon={<ExpandMore />} data-testid='add-event-button' onClick={handleClick}>
        {t('SchedulerView.createEvent.title')}
      </Button>
      <AddEventDropdownList
        anchorEl={anchorEl}
        schedulerType={schedulerType}
        open={open}
        handleClose={handleClose}
        handleEventClick={handleEventClick}
      />
    </>
  );
}

export default AddEventDropdown;
